/**
 * Live-feed socket seam for the Admin Operational Overview dashboard
 * (ODE-P07-T01).
 *
 * Browser WebSocket clients connect to GET /v1/admin/overview/stream — the
 * server pushes `{ type: 'overview.snapshot', snapshot }` frames (initial
 * snapshot + one per update). The socket is only opened after the snapshot
 * fetch succeeded (fail-closed: never open a feed with unknown/absent
 * authorization). `createDefaultSocket` is the production factory; tests
 * inject a fake `WebSocketLike` through the dashboard's `createSocket`
 * seam — no real sockets in the test environment.
 *
 * Frame parsing is strict: a malformed frame yields null and is ignored — a
 * broken/foreign message can never corrupt the rendered operational state.
 *
 * Traces to: ODE-PRD-REQ-043, ODE-PRD-REQ-044, ODE-PRD-REQ-090.
 */

import type { OverviewSnapshot } from '../../api/client.js';

/** Minimal WebSocket surface used by the hook (injectable for tests). */
export interface WebSocketLike {
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { readonly data: unknown }) => void) | null;
  onclose: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  close(): void;
}

/** Live-feed lifecycle rendered as the REQ-090 live/disconnected states. */
export type OverviewFeedStatus = 'connecting' | 'live' | 'disconnected';

/**
 * Production factory: a real browser WebSocket. Returns null when no
 * WebSocket global exists (non-browser context) — the dashboard then
 * renders the disconnected state instead of crashing.
 */
export function createDefaultSocket(url: string): WebSocketLike | null {
  if (typeof WebSocket === 'undefined') return null;
  return new WebSocket(url) as unknown as WebSocketLike;
}

/** True when the value looks like a server overview snapshot (wire gate). */
export function isOverviewSnapshotWire(value: unknown): value is OverviewSnapshot {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.schemaVersion === 'number' &&
    typeof record.asOfEpochMs === 'number' &&
    typeof record.staleAfterMs === 'number' &&
    typeof record.kpi === 'object' &&
    record.kpi !== null &&
    Array.isArray(record.livePositions) &&
    Array.isArray(record.doStatus) &&
    typeof record.recap === 'object' &&
    record.recap !== null
  );
}

/**
 * Parses one live-feed frame. Malformed frames (bad JSON, foreign type,
 * snapshot failing the wire gate) yield null — the caller ignores them and
 * keeps the last good snapshot; nothing is ever fabricated from a bad
 * message.
 */
export function parseStreamFrame(raw: unknown): OverviewSnapshot | null {
  if (typeof raw !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const frame = parsed as Record<string, unknown>;
  if (frame.type !== 'overview.snapshot') return null;
  return isOverviewSnapshotWire(frame.snapshot) ? frame.snapshot : null;
}
